import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TYPES, TYPES_MAP } from '@a0/support-center-common/constants/tickets/types';

import { StyledReactSelect as Select } from '../../../common/components/StyledReactSelect';
import FormGroup from 'react-bootstrap/FormGroup';
import { Auth0Notification } from '@a0/support-center-components';

// This component asks the ticket type, possibly in two stages.
// If the first-level question has sub options, a second dropdown is presented
// to the user.
export const firstLevelOptions = [
  {
    value: "Subscription",
    text: "Subscription Questions or Issues",
    description: "Questions related to billing/payments, compliance/legal, or self-service entitlements",
    subOptions: [
      TYPES_MAP[TYPES.BILLING],
      TYPES_MAP[TYPES.COMPLIANCE_LEGAL],
      TYPES_MAP[TYPES.SELF_SERVICE_ENTITLEMENTS]
    ]
  },
  {
    value: "Administrators",
    text: "Administrator Questions or Issues",
    description: "Questions and issues related to Auth0 Dashboard Tenant Members and Auth0 Teams",
    subOptions: [
      TYPES_MAP[TYPES.TENANT_MEMBER_MFA_RESET],
      TYPES_MAP[TYPES.DELETE_ACCOUNT],
      TYPES_MAP[TYPES.AUTH0_TEAMS],
      TYPES_MAP[TYPES.DASHBOARD_ADMIN_ACCOUNT],
      TYPES_MAP[TYPES.DASHBOARD_SSO_REQUEST]
    ]
  },
  TYPES_MAP[TYPES.PEN_TESTING_REQUEST],
  TYPES_MAP[TYPES.NEW_FEATURE_REQUEST],
  TYPES_MAP[TYPES.EXPORT_PASSWORD_HASHES],
  TYPES_MAP[TYPES.PRODUCT_SUPPORT]
];

/**
 * New Ticket Type field component
 */
const Type = ({ name, value, onChange }) => {

  // find the first and second level selections (full objects) based on the value provided
  const derivedFirstValue = firstLevelOptions.find(option => option.value === value || option.subOptions && option.subOptions.some(subOption => subOption.value === value)) || null;
  const derivedSecondValue = derivedFirstValue && derivedFirstValue.subOptions && derivedFirstValue.subOptions.find(option => option.value === value) || null;

  // this keeps an internal state for the first dropdown selection
  // while there's no selected second value
  const [ firstDropdownManualSelection, setFirstDropdownManualSelection ] = useState();

  const firstValue = derivedFirstValue || firstDropdownManualSelection;

  const [ subOptions, setSubOptions ] = useState();
  useEffect(() => {
    // the available subOptions are set in an useEffect hook
    // so that when switching between different first-level options
    // the subOptions are first cleared and the second <Select> component
    // is first removed from the tree and then added back,
    // so that "defaultMenuIsOpen" is reset
    if (firstValue && firstValue.subOptions && !subOptions) {
      setSubOptions(firstValue.subOptions);
    }
  }, [firstValue]);

  const notifyChanges = (item) => {
    const selectedType = item.subOptions ? undefined : item.value;
    const event = {
      target: {
        name,
        value: selectedType
      }
    };
    onChange(event);
  };

  const handleFirstClick =(item) => {
    // if the item doesn't have sub options we can clear the manual
    // selection state, because the external "value" prop will be updated
    // and the first dropdown state can be derived from the value
    setFirstDropdownManualSelection(item.subOptions ? item : null);
    setSubOptions(null);
    notifyChanges(item);
  };

  const handleSecondClick =(item) => {
    setFirstDropdownManualSelection(null);
    notifyChanges(item);
  };

  const formatOptionLabelWithDescription = (item) => (
    <React.Fragment>
      <strong>{item.text}</strong>
      <br/>
      {item.description}
    </React.Fragment>
  );

  return (
    <React.Fragment>
    <FormGroup className="form-group">
      <label htmlFor={name} className="animated fadeIn">What can we help you with? (*)
        {!firstValue &&
          <Auth0Notification type="primary">
            Please note we have made some changes to our Support Case fields in the interest of more efficiently routing your issue to the right help.
          </Auth0Notification>
        }
      </label>
      <Select
        options={firstLevelOptions}
        value={firstValue}
        onChange={handleFirstClick}
        formatOptionLabel={formatOptionLabelWithDescription}
        initialDisplayAsList
        noHeightLimit
        isSearchable={false}
      />
      </FormGroup>
    {subOptions &&
      <FormGroup className="form-group">
        <label htmlFor={name} className="animated fadeIn">What best describes your question or issue? (*)</label>
        <Select
          options={subOptions}
          value={derivedSecondValue}
          onChange={handleSecondClick}
          formatOptionLabel={formatOptionLabelWithDescription}
          initialDisplayAsList
          noHeightLimit
          isSearchable={false}
        />
      </FormGroup>
    }
    </React.Fragment>
  );
};

Type.propTypes = {
  /** The form name and schema key */
  name: PropTypes.string.isRequired,
  /** Callback for change event */
  onChange: PropTypes.func.isRequired,
  /** The current field value */
  value: PropTypes.string.isRequired,
  /** The selected subscription object */
  selectedSubscription: PropTypes.object.isRequired
};

export default Type;
